
import React, { useState, useEffect } from 'react';

export default function HomePage() {
  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${import.meta.env.VITE_SITE_URL}#organization`,
          "name": "SHOCKSYS",
          "url": import.meta.env.VITE_SITE_URL,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/360cad6688a1498f12fb9bade96a6b04/f622c25b17b470f88798c36ef1e507db.png"
          },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Franca",
            "addressRegion": "SP",
            "addressCountry": "BR"
          }
        },
        {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL}#website`,
          "url": import.meta.env.VITE_SITE_URL,
          "name": "SHOCKSYS - Detector de Vibração Sísmico",
          "description": "Sistema modular de detecção sísmica SH-V-10 com até 10 sensores para proteção máxima contra invasões em cofres, ATMs e áreas críticas.",
          "publisher": {
            "@id": `${import.meta.env.VITE_SITE_URL}#organization`
          },
          "inLanguage": "pt-BR"
        },
        {
          "@type": "Product",
          "@id": `${import.meta.env.VITE_SITE_URL}#product`,
          "name": "SH-V-10 Detector Sísmico",
          "description": "Sistema modular de detecção sísmica com até 10 sensores para proteção máxima contra invasões em cofres, ATMs e áreas críticas.",
          "brand": {
            "@type": "Brand",
            "name": "SHOCKSYS"
          },
          "manufacturer": {
            "@id": `${import.meta.env.VITE_SITE_URL}#organization`
          },
          "category": "Sistemas de Segurança",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "BRL",
            "seller": {
              "@id": `${import.meta.env.VITE_SITE_URL}#organization`
            }
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "O que é o sistema SH-V-10?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "O SH-V-10 é um sistema modular de detecção sísmica que pode conectar até 10 sensores para proteção máxima contra invasões em cofres, ATMs e áreas críticas."
              }
            },
            {
              "@type": "Question",
              "name": "Qual é a garantia do produto?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Oferecemos 5 anos de garantia para todos os nossos sistemas de detecção sísmica."
              }
            },
            {
              "@type": "Question",
              "name": "Vocês fazem entrega em todo o Brasil?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sim, fazemos entrega em todo o território nacional com frete grátis."
              }
            }
          ]
        }
      ]
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Initialize Calendly widget
  useEffect(() => {
    // Load Calendly script if not already loaded
    if (!document.querySelector('script[src*="calendly.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }

    const initCalendly = () => {
      if ((window as any).Calendly) {
        // Clear any existing widget first
        const existingWidget = document.querySelector('.calendly-inline-widget');
        if (existingWidget) {
          existingWidget.innerHTML = '';
        }

        (window as any).Calendly.initInlineWidget({
          url: 'https://calendly.com/contato-shocksys/30min',
          parentElement: document.querySelector('.calendly-inline-widget'),
          prefill: {},
          utm: {}
        });
      } else {
        // Retry after a short delay if Calendly is not loaded yet
        setTimeout(initCalendly, 200);
      }
    };

    // Wait for the component to mount and then initialize
    const timer = setTimeout(initCalendly, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Added `company` field to match the optional input in the form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company: '',
  });
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  // Add Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Product Schema
    const productSchema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      'name': 'Detector de Vibração Sísmico SH-V-10',
      'description': 'Sistema modular de detecção sísmica com até 10 sensores para proteção máxima contra invasões e arrombamentos em cofres, ATMs e áreas críticas',
      'brand': {
        '@type': 'Brand',
        'name': 'SHOCKSYS'
      },
      'image': 'https://static.readdy.ai/image/360cad6688a1498f12fb9bade96a6b04/f622c25b17b470f88798c36ef1e507db.png',
      'offers': {
        '@type': 'Offer',
        'url': siteUrl,
        'priceCurrency': 'BRL',
        'price': '2200.00',
        'availability': 'https://schema.org/InStock',
        'seller': {
          '@type': 'Organization',
          'name': 'SHOCKSYS'
        }
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.8',
        'reviewCount': '127'
      }
    };

    // Organization Schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'SHOCKSYS',
      'url': siteUrl,
      'logo': 'https://static.readdy.ai/image/360cad6688a1498f12fb9bade96a6b04/f622c25b17b470f88798c36ef1e507db.png',
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+55-16-98140-0236',
        'contactType': 'customer service',
        'email': 'contato@shocksys.com.br'
      },
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'R. Cássia, 1160 - Jardim Francano',
        'addressLocality': 'Franca',
        'addressRegion': 'SP',
        'postalCode': '14405-016',
        'addressCountry': 'BR'
      }
    };

    // Website Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'SHOCKSYS',
      'url': siteUrl,
      'description': 'Sistema de deteção sísmica SH-V-10 para segurança avançada.',
      'inLanguage': 'pt-BR'
    };

    // FAQ Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Quantos sensores o sistema SH-V-10 suporta?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'O sistema SH-V-10 suporta até 10 sensores sísmicos conectados em loop de dois fios com até 70 metros de comprimento total.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Qual a faixa de temperatura de operação?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'O sistema opera em condições extremas, de -30°C a +50°C, garantindo proteção em qualquer ambiente.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Quais aplicações são recomendadas?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Ideal para cofres e salas-cofre, caixas eletrônicos (ATMs), instalações comerciais como joalherias e áreas críticas como datacenters.'
          }
        }
      ]
    };

    // Add schemas to head
    const addSchema = (schema: any, id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(productSchema, 'product-schema');
    addSchema(organizationSchema, 'organization-schema');
    addSchema(websiteSchema, 'website-schema');
    addSchema(faqSchema, 'faq-schema');

    return () => {
      // Cleanup schemas on unmount
      ['product-schema', 'organization-schema', 'website-schema', 'faq-schema'].forEach(id => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataObj = new FormData(formElement);

      const response = await fetch(
        'https://readdy.ai/api/form/d44cpkmpguuav66rged0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formDataObj as any).toString(),
        }
      );

      if (response.ok) {
        alert('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          company: '',
        });
      } else {
        throw new Error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePurchase = async () => {
    setIsCheckoutLoading(true);

    try {
      // Redirect directly to Asaas payment page
      window.open('https://www.asaas.com/c/o3p28epfqskyp0p3', '_blank');
    } catch (error: any) {
      console.error('Erro no checkout:', error);
      alert(`Erro ao processar pagamento: ${error.message}. Tente novamente.`);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <header className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-cyan-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-center">
                <img
                  src="https://static.readdy.ai/image/360cad6688a1498f12fb9bade96a6b04/f622c25b17b470f88798c36ef1e507db.png"
                  alt="SHOCKSYS"
                  className="h-24 lg:h-30 mb-4 mx-auto"
                />
                <br />
                Detector de Vibração Sísmico
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Sistema modular de detecção sísmica SH-V-10 com tecnologia avançada
                para proteção máxima contra invasões e arrombamentos em cofres,
                ATMs e áreas críticas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handlePurchase}
                  disabled={isCheckoutLoading}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {isCheckoutLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Processando...
                    </>
                  ) : (
                    <>
                      <i className="ri-shopping-cart-line"></i>
                      Comprar Agora - R$ 2.200,00
                    </>
                  )}
                </button>
                <a
                  href="https://wa.me/5516981400236?text=Gostaria%20de%20mais%20informações%20do%20ShockSys."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
                >
                  Solicitar Orçamento
                </a>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-cyan-400"></i>
                  <span>Garantia 5 anos</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-truck-line text-cyan-400"></i>
                  <span>Frete grátis Brasil</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-secure-payment-line text-cyan-400"></i>
                  <span>Pagamento seguro</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 backdrop-blur-sm border border-cyan-500/30">
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-700/30">
                  <iframe
                    src="https://www.youtube.com/embed/r5QNosRJ73o?enablejsapi=1&origin=https://shocksys.com.br"
                    title="SHOCKSYS - Sistema de Detecção Sísmico SH-V-10"
                    className="w-full h-full rounded-lg"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Investimento em <span className="text-cyan-600">Segurança</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sistema completo com tecnologia de ponta para proteção máxima do seu
              patrimônio
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border-2 border-cyan-500 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-center py-3 font-semibold">
                🏆 MAIS VENDIDO
              </div>

              <div className="p-8 pt-16">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    SH-V-10 Completo
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Sistema modular com até 10 sensores
                  </p>

                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">
                      R$ 2.200
                    </span>
                    <span className="text-gray-600">,00</span>
                  </div>

                  <div className="text-sm text-gray-500 mb-6">
                    ou 12x de R$ 183,33 sem juros
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    "Módulo processador de sinal",
                    "Até 10 sensores sísmicos",
                    "Cabo de conexão 70m",
                    "Manual técnico completo",
                    "Garantia de 5 anos",
                    "Suporte técnico especializado",
                    "Frete grátis para todo Brasil"
                  ].map(item => (
                    <div key={item} className="flex items-center gap-3">
                      <i className="ri-check-line text-cyan-500 text-xl"></i>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => window.open('https://www.asaas.com/c/o3p28epfqskyp0p3', '_blank')}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-secure-payment-line"></i>
                  Comprar com Segurança
                </button>

                <div className="mt-6 text-center">
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-bank-card-line"></i>
                      <span>Cartão</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-file-text-line"></i>
                      <span>Boleto</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-shield-check-line"></i>
                      <span>Pix</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Pagamento 100% seguro
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section
        id="products"
        className="py-20 px-4 md:px-6 bg-slate-800 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px]"></div>
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">
              Sistema{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Modular
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <article className="group">
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm hover:border-cyan-500/40 transition-all duration-500 h-full p-8">
                <div className="aspect-square rounded-lg overflow-hidden mb-6 bg-slate-700/30 relative">
                  <img
                    src="https://shocksys210-ap3mkak2.manus.space/product-system.jpg"
                    alt="Módulo Processador de Sinal (SPM)"
                    title="Módulo Processador de Sinal detector sísmico"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white group-hover:text-cyan-400 transition-colors">
                    Módulo Processador de Sinal (SPM)
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Central de processamento com tecnologia avançada
                  </p>
                </div>
              </div>
            </article>

            <article className="group">
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm hover:border-cyan-500/40 transition-all duration-500 h-full p-8">
                <div className="aspect-square rounded-lg overflow-hidden mb-6 bg-slate-700/30 relative">
                  <img
                    src="https://shocksys210-ap3mkak2.manus.space/product-sensor.jpg"
                    alt="Sensor Sísmico"
                    title="Sensor sísmico detector vibração para segurança"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white group-hover:text-cyan-400 transition-colors">
                    Sensor Sísmico
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Sensor de vibração com tecnologia piezoelétrica para detecção precisa
                  </p>
                </div>
              </div>
            </article>
          </div>

          {/* Connection Diagram Section */}
          <div className="mt-16 space-y-8">
            <div className="text-center">
              <h3 className="text-2xl md:text-4xl font-bold text-white mb-4">
                Diagrama de{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-401 bg-clip-text text-transparent">
                  Conexão
                </span>
              </h3>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                Esquema de ligação dos sensores sísmicos ao módulo processador
                de sinal via loop de dois fios
              </p>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm p-8">
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-white/95 relative">
                <img
                  src="https://static.readdy.ai/image/360cad6688a1498f12fb9bade96a6b04/91fdaf6f8c7a1adf3d9f82e2a0857a33.jpeg"
                  alt="Diagrama de Conexão dos Sensores Sísmicos SH-V-10"
                  title="Esquema ligação sensores sísmicos sistema segurança"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                {[
                  {
                    title: "Conexão em Loop",
                    description: "Até 10 sensores conectados em série via cabo de dois fios"
                  },
                  {
                    title: "Distância Máxima",
                    description: "Loop de até 70 metros de comprimento total"
                  },
                  {
                    title: "Alimentação",
                    description: "Alimentação e sinal transmitidos pelo mesmo cabo"
                  }
                ].map(item => (
                  <div key={item.title} className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/5">
                    <h4 className="text-lg font-semibold mb-2 text-cyan-400">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 md:px-6 bg-slate-900/50 relative overflow-hidden">
        <div className="container relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: "ri-shield-check-line", value: "10", label: "Zonas de Controle" },
              { icon: "ri-temp-cold-line", value: "-30°C a +50°C", label: "Faixa de Temperatura" },
              { icon: "ri-time-line", value: "24/7", label: "Monitoramento" }
            ].map(stat => (
              <div key={stat.label} className="relative">
                <div className="relative p-8 rounded-xl overflow-hidden group">
                  <div className="absolute inset-0 bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl group-hover:border-cyan-500/30 transition-colors"></div>
                  <div className="relative flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                      <i className={`${stat.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="text-4xl md:text-5xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-slate-400 font-medium tracking-wide uppercase">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <main className="py-20 px-4 md:px-6 bg-slate-800 relative overflow-hidden">
        <div className="container space-y-20 relative z-10">
          {/* Intro */}
          <div className="space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                SH-V-10:{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Proteção Inteligente
                </span>
              </h2>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                Sistema modular composto por um Módulo Processador de Sinal
                (SPM) e até 10 sensores sísmicos, oferecendo cobertura
                completa para áreas críticas.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-compasses-2-line",
                  title: "Design Compacto",
                  text: "Módulo SPM: 62 x 40 x 32 mm | Sensores: Design discreto para instalação em qualquer superfície"
                },
                {
                  icon: "ri-tools-line",
                  title: "Instalação Simplificada",
                  text: "Conexão via loop de dois fios (até 70m) com alimentação e sinal no mesmo cabo"
                },
                {
                  icon: "ri-file-text-line",
                  title: "Documentação Completa",
                  text: "Guia de instalação detalhado e suporte técnico inclusos"
                }
              ].map(feature => (
                <article key={feature.title} className="rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm hover:border-cyan-500/40 transition-all duration-300 p-6 h-full group">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                  <p className="text-slate-400">{feature.text}</p>
                </article>
              ))}
            </div>
          </div>

          {/* Kit Contents */}
          <section className="space-y-8">
            <h3 className="text-2xl md:text-4xl font-bold text-center text-white">
              Conteúdo do Kit
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Sistema SH-V-10",
                  description: "1 Módulo Processador de Sinal (SPM) + Sensores configuráveis (10 unidades)"
                },
                {
                  title: "Fixadores",
                  description: "Kit completo de fixação para montagem em diversas superfícies"
                },
                {
                  title: "Documentação",
                  description: "Manual operacional completo e guia de instalação detalhado"
                }
              ].map(item => (
                <div key={item.title} className="p-6 rounded-xl bg-slate-700/30 border border-slate-600/5 hover:border-cyan-300 transition-all duration-300">
                  <h4 className="text-lg font-semibold mb-2 text-cyan-400">{item.title}</h4>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Security Features */}
          <section className="space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                Recursos de{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Segurança Premium
                </span>
              </h2>
              <p className="text-lg text-slate-300">
                Tecnologia de ponta para proteção total contra invasões e
                arrombamentos
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-radar-line",
                  title: "Detecção Avançada",
                  text: "Identifica tentativas de destruição em concreto (≥12cm), tijolo (≥15cm), madeira, metal e outras estruturas sólidas"
                },
                {
                  icon: "ri-shield-line",
                  title: "Alta Imunidade",
                  text: "Resistente a ruídos acústicos, impactos acidentais, vibrações de elevadores, transporte e sistemas de ventilação"
                },
                {
                  icon: "ri-alarm-warning-line",
                  title: "Sistema de Alarme",
                  text: "Relé de saída com sinal de alarme, controle de violação de tampa e detecção de falhas no sistema"
                }
              ].map(item => (
                <article key={item.title} className="rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm hover:border-cyan-500/40 transition-all duration-300 p-6 h-full group">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{item.title}</h3>
                  <p className="text-slate-400">{item.text}</p>
                </article>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Operação Extrema",
                  description: "Funciona em condições adversas, de -30°C a +50°C, garantindo proteção em qualquer ambiente"
                },
                {
                  title: "Controle de Integridade",
                  description: "Monitora tampas de sensores, falhas em loops e desconexão sensores automaticamente"
                },
                {
                  title: "Fácil Configuração",
                  description: "DIP-switches para modo de teste, indicação de eventos e controle de sensores conectados"
                }
              ].map(item => (
                <div key={item.title} className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/5">
                  <h4 className="text-lg font-semibold mb-3 text-cyan-400">{item.title}</h4>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Call to Action */}
          <div className="text-center mt-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5516981400236?text=Gostaria%20de%20mais%20informações%20do%20ShockSys."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Solicitar Orçamento
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Aplicações Estratégicas */}
      <section className="py-16 bg-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5"></div>
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Aplicações Estratégicas do SH-V-10
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Proteção especializada para diversos setores críticos com tecnologia de ponta.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Lista da Esquerda */}
              <div className="space-y-4">
                {[
                  {
                    icon: "ri-bank-line",
                    title: "Bancos",
                    description: "Agências bancárias, cofres e áreas de alta segurança"
                  },
                  {
                    icon: "ri-diamond-line",
                    title: "Joalherias",
                    description: "Estabelecimentos com produtos de alto valor"
                  },
                  {
                    icon: "ri-bank-card-line",
                    title: "Caixas Eletrônicos (ATMs)",
                    description: "Proteção contra ataques físicos e violação"
                  },
                  {
                    icon: "ri-server-line",
                    title: "Datacenters",
                    description: "Infraestrutura de TI e ambientes sensíveis"
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/5 hover:border-cyan-500/40 transition-all duration-300">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Lista da Direita */}
              <div className="space-y-4">
                {[
                  {
                    icon: "ri-government-line",
                    title: "Instituições Governamentais",
                    description: "Instalações públicas e documentos confidenciais"
                  },
                  {
                    icon: "ri-medicine-bottle-line",
                    title: "Farmácias Hospitalares",
                    description: "Controle de medicamentos e substâncias regulamentadas"
                  },
                  {
                    icon: "ri-safe-2-line",
                    title: "Empresas de Valores",
                    description: "Transporte e armazenamento de valores"
                  },
                  {
                    icon: "ri-building-line",
                    title: "Instalações Comerciais",
                    description: "Lojas, escritórios e áreas comerciais críticas"
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/5 hover:border-cyan-500/40 transition-all duration-300">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Benefícios Resumidos */}
          <div className="mt-12 bg-slate-700/30 rounded-2xl p-8 border border-slate-600/5">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Por que escolher o SH-V-10?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Tecnologia Avançada</h4>
                <p className="text-sm text-slate-300">
                  Sensores de última geração com IA integrada para detecção precisa.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-24-hours-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Suporte 24/7</h4>
                <p className="text-sm text-slate-300">
                  Monitoramento contíno e suporte técnico especializado.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Garantia Total</h4>
                <p className="text-sm text-slate-300">
                  5 anos de garantia com manutenção preventiva incluída.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5516981400236?text=Gostaria%20de%20mais%20informações%20do%20ShockSys."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Solicitar Orçamento
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Agendamento via Calendly */}
      <section id="agendamento" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Agende uma <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Demonstração</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Veja o SH-V-10 em ação! Agende uma demonstração personalizada e descubra 
              como nosso sistema pode proteger seu negócio de forma inteligente e eficaz.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Widget do Calendly */}
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                Escolha o melhor horário
              </h3>
              <div 
                className="calendly-inline-widget bg-white rounded-xl overflow-hidden" 
                data-url="https://calendly.com/contato-shocksys/30min"
                style={{
                  minWidth: '100%',
                  width: '100%',
                  height: '630px'
                }}
              ></div>
            </div>

            {/* Formulário de Backup */}
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <h3 className="text-2xl font-bold text-white mb-6">
                Ou solicite um contato
              </h3>
              <p className="text-slate-300 mb-8">
                Preencha o formulário abaixo e nossa equipe entrará em contato para agendar 
                sua demonstração no melhor horário para você.
              </p>

              <form 
                id="agendamento-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d44edmpeds0ugd97450g"
                method="POST"
                className="space-y-6"
                onSubmit={async (e) => {
                  e.preventDefault();
                  
                  try {
                    const formElement = e.target as HTMLFormElement;
                    const formDataObj = new FormData(formElement);
                    
                    const response = await fetch('https://readdy.ai/api/form/d44edmpeds0ugd97450g', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formDataObj as any).toString(),
                    });

                    if (response.ok) {
                      alert('Solicitação enviada com sucesso! Entraremos em contato em breve.');
                      formElement.reset();
                    } else {
                      throw new Error('Erro ao enviar solicitação');
                    }
                  } catch (error) {
                    console.error('Erro:', error);
                    alert('Erro ao enviar solicitação. Tente novamente.');
                  }
                }}
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-semibold text-slate-300 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      required
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500
0 focus:border-transparent transition-all duration-300"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div>
                    <label htmlFor="empresa" className="block text-sm font-semibold text-slate-300 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-5
0 focus:border-transparent transition-all duration-300"
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-slate-700/50 border 
border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring 
-cyan-500 focus:border-transparent transition-all duration-300"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-semibold text-slate-300 mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      required
                      className="w-full px-4 py-3 bg-slate-700/50 border 
border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring 
-cyan-500 focus:border-transparent transition-all duration-300"
                      placeholder="(xx) xxxx-xxxx"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="segmento" className="block text-sm font-semibold text-slate-300 mb-2">
                    Segmento de Atuação
                  </label>
                  <div className="relative">
                    <select
                      id="segmento"
                      name="segmento"
                      className="w-full px-4 py-3 pr-8 bg-slate-700/50 border 
border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-cyan-500 
focus:border-transparent transition-all duration-300 appearance-none"
                    >
                      <option value="">Selecione seu segmento</option>
                      <option value="banco">Banco</option>
                      <option value="joalheria">Joalheria</option>
                      <option value="atm">Caixas Eletrônicos (ATMs)</option>
                      <option value="datacenter">Datacenter</option>
                      <option value="governo">Instituição Governamental</option>
                      <option value="farmacia">Farmácia Hospitalar</option>
                      <option value="valores">Empresa de Valores</option>
                      <option value="outro">Outro</option>
                    </select>
                    <div className="absolute inset-y-0 right 
0 flex items-center pr-3 pointer-
events-none">
                      <i className="ri-arrow-down-s-line text-slate-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="horario_preferencia" className="block text-sm font-semibold text-slate-300 mb-2">
                    Horário de Preferência
                  </label>
                  <div className="grid grid-cols-2 md:grid-
cols-4 gap-3">
                    {[
                      { label: "Manhã", value: "manha" },
                      { label: "Tarde", value: "tarde" },
                      { label: "Noite", value: "noite" },
                      { label: "Flexível", value: "flexivel" }
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-2 cursor-pointer">
                        <input type="radio" name="horario_preferencia" value={option.value} className="text-cyan-500" />
                        <span className="text-sm text-slate-300">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-sm font-semibold text-slate-300 mb-2">
                    Mensagem Adicional
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-slate-700/50 border 
border-slate-600 rounded-xl text-white placeholder-slate-400 
focus:ring-2 focus:ring-cyan-500 focus:border-
transparent transition-all duration-300 resize-none"
                    placeholder="Conte-nos mais sobre suas necessidades de segurança..."
                  ></textarea>
                  <p className="text-xs text-slate-400 mt-1">Máximo 500 caracteres</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-
xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  Solicitar Agendamento
                </button>
              </form>

              <div className="mt-8 p-6 bg-slate-700/30 rounded-xl border border-slate-600/50">
                <h4 className="font-semibold text-white mb-3">O que esperar da demonstração:</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-400 mr-2"></i>
                    Apresentação completa do sistema SH-V-10
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-400 mr-2"></i>
                    Análise personalizada para seu negócio
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-400 mr-2"></i>
                    Orçamento detalhado sem compromisso
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-400 mr-2"></i>
                    Suporte técnico especializado
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Perguntas{' '}
              <span className="text-cyan-300 font-bold">
                Frequentes
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Tire suas principais dúvidas sobre o sistema SH-V-10
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Quantos sensores o sistema SH-V-10 suporta?",
                answer: "O sistema SH-V-10 suporta até 10 sensores sísmicos conectados em loop de dois fios com até 70 metros de comprimento total."
              },
              {
                question: "Qual a faixa de temperatura de operação?",
                answer: "O sistema opera em condições extremas, de -30°C a +50°C, garantindo proteção em qualquer ambiente."
              },
              {
                question: "Quais aplicações são recomendadas?",
                answer: "Ideal para cofres e salas-cofre, caixas eletrônicos (ATMs), instalações comerciais como joalherias e áreas críticas como datacenters."
              },
              {
                question: "O sistema é resistente a falsos alarmes?",
                answer: "Sim, possui alta imunidade a ruídos acústicos, impactos acidentais, vibrações de elevadores, transporte e sistemas de ventilação."
              },
              {
                question: "Qual é a garantia do produto?",
                answer: "Oferecemos 5 anos de garantia para todos os nossos sistemas de detecção sísmica."
              },
              {
                question: "Vocês fazem entrega em todo o Brasil?",
                answer: "Sim, fazemos entrega em todo o território nacional com frete grátis."
              }
            ].map((faq, index) => (
              <article key={index} className="bg-slate-700/30 rounded-xl border border-slate-600/50 overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-700/50 transition-colors">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-add-line text-cyan-400 text-xl group-open:rotate-45 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 md:px-6">
        <div className="container">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
            {/* Logo e Descrição */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src="https://public.readdy.ai/ai/img_res/319b2c74-37d1-49b2-81b1-b5e53e3cc397.png"
                  alt="SHOCKSYS"
                  className="h-72 w-auto"
                />
              </div>

              <p className="text-gray-400 mb-4 text-sm leading-relaxed">
                Sistema de detecção sísmica para proteção de ativos em cofres,
                ATMs, lojas e instalações críticas com tecnologia avançada e
                certificação internacional.
              </p>

              {/* Redes Sociais */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-white mb-3">Siga-nos nas redes sociais</h4>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/profile.php?id=61583226816268"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-700 hover:bg-blue-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                    title="Siga-nos no Facebook"
                  >
                    <i className="ri-facebook-fill text-lg text-white"></i>
                  </a>
                  <a
                    href="https://www.instagram.com/shocksys10/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                    title="Siga-nos no Instagram"
                  >
                    <i className="ri-instagram-fill text-lg text-white"></i>
                  </a>
                  <a
                    href="https://wa.me/5516981400236?text=Gostaria%20de%20mais%20informações%20do%20ShockSys."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-700 hover:bg-green-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                    title="Fale conosco no WhatsApp"
                  >
                    <i className="ri-whatsapp-fill text-lg text-white"></i>
                  </a>
                  <a
                    href="mailto:contato@shocksys.com.br"
                    className="w-10 h-10 bg-slate-700 hover:bg-red-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                    title="Envie um e-mail"
                  >
                    <i className="ri-mail-fill text-lg text-white"></i>
                  </a>
                </div>
              </div>

              {/* Botão de Download do Manual */}
              <a 
                href="https://drive.google.com/file/d/1kLcftOfAN50WewXRx6ni751bNUrC0-DT/view?usp=drive_link" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-
600 hover:to-red-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-
all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer group"
              >
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-
colors">
                  <i className="ri-download-cloud-line text-sm"></i>
                </div>
                <span>Baixar Manual Técnico</span>
                <i className="ri-external-link-line text-xs opacity-70 group-hover:opacity-100 transition-
opacity"></i>
              </a>
            </div>

            {/* Links de Navegação */}
            <nav>
              <h4 className="text-lg font-semibold mb-3">Links</h4>
              <ul className="space-y-1">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Home
                  </a>
                </li>
                <li>
                  <a href="#products" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Produtos
                  </a>
                </li>
                <li>
                  <a href="#contato" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Contato
                  </a>
                </li>
              </ul>
            </nav>

            {/* Informações de Contato */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Contato</h4>
              <div className="space-y-1">
                <p className="text-gray-400 text-sm">contato@shocksys.com.br</p>
                <a 
                  href="https://wa.me/5516981400236?text=Estou%20com%20d%C3%ADvidas%20relacionadas%20ao%20ShockSys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-green-400 transition-colors cursor-pointer text-sm block"
                >
                  +55 16 98140-0236
                </a>
              </div>
            </div>
          </div>

          {/* Linha de Separação e Copyright */}
          <div className="border-t border-gray-800 mt-6 pt-4 text-center text-gray-400">
            <p className="text-sm">&copy; 2024 SHOCKSYS. Todos os direitos reservados.</p>
            <p className="mt-1 text-xs">
              R. Cássia, 1160 - Jardim Francano, Franca - SP, 14405-016 | CNPJ:60.111.763/0001-31
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
